import {
  ContentDeciderContainer,
  Loader,
  DateTimeFormatter,
  P2T2,
  ALL_MODULES,
  generateString,
  ImagePreviewModal,
  withErrorBoundary
} from '@nile/components';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { Avatar, Button } from '@superops/ui-design-system';
import envConfig from '@nile/home/src/Config/env';
import { GET_MY_MENTIONS, AccountClient } from '@nile/graphql';
import dompurify from 'dompurify';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import {
  WidgetWrapper,
  Header,
  Title,
  Content,
  Actions
} from '../HomeWidgetContainer';
import { dompurifier } from '@nile/components/src/Utils/helpers';

function MentionViewWidget(props) {
  const { widget, user, meta, deleteWidget } = props;
  const tabContext = useContext(TabContext);
  const [mentions, setMentions] = useState([]);
  const [loading, setLoading] = useState(!!widget);

  const options = {
    pageSize: 5,
    page: 1,
    sort: [
      {
        attribute: 'time',
        order: 'DESC'
      }
    ]
  };

  const fetchViewData = async (personalization, pageOptions) => {
    setLoading(true);
    const response = await AccountClient.query({
      query: GET_MY_MENTIONS,
      variables: {
        listInfo: pageOptions ? pageOptions : options
      },
      fetchPolicy: 'network-only'
    });
    setMentions(response?.data?.getMyMentions?.mentions);
    setLoading(false);
  };

  useEffect(() => {
    fetchViewData();
  }, []);

  const getWorkItemTitle = (workItem) => {
    if (workItem?.subject) return workItem?.subject;
    if (workItem?.displayId)
      return `${workItem.module.toLowerCase()} #${workItem?.displayId}`;
    return workItem?.workId;
  };

  const ModuleMap = {
    PROJECT: 'PROJECT_DETAIL',
    INVOICE: 'INVOICE',
    QUOTE: 'QUOTE'
  };

  const getMentionCard = (mention) => {
    switch (mention.type) {
      case 'NOTE':
        return (
          <MentionItem
            time={mention.time}
            title={
              mention.note?.workItem
                ? getWorkItemTitle(mention.note?.workItem)
                : mention.note?.ticket?.subject
            }
            content={mention.note?.content}
            addedBy={mention.note?.addedBy}
            handleAction={() => {
              const workItem = mention.note?.workItem;
              tabContext.openTab({
                module: ModuleMap[workItem?.module] || 'TICKET',
                data: {
                  [`${
                    workItem?.module ? workItem.module.toLowerCase() : 'ticket'
                  }Id`]: workItem?.workId || mention.note?.ticket?.ticketId
                }
              });
            }}
          />
        );
      case 'PROJECT_CONVERSATION':
        return (
          <MentionItem
            time={mention.time}
            title={mention.projectConversation?.project?.title}
            content={mention.projectConversation?.content}
            addedBy={mention.projectConversation?.addedBy}
            handleAction={() =>
              tabContext.openTab({
                module: ALL_MODULES.projectDetail,
                data: {
                  projectId: mention.projectConversation?.project?.projectId,
                  title: mention.projectConversation?.project?.title
                }
              })
            }
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <WidgetWrapper>
        <div className="widget-header flex center fg1">
          <Actions className="widget-actions visibilityActions">
            <div onClick={deleteWidget.bind(this, widget)} className="ptr">
              <SVGIcon name="Delete" />
            </div>
          </Actions>
        </div>
        <Loader />
      </WidgetWrapper>
    );
  }
  return (
    <WidgetWrapper>
      <Header>
        <Title>
          <FormattedMessage id="admin.home.widgetlibrary.mentions.at" />
        </Title>
        <div className="widget-header flex center fg1">
          <Actions className="widget-actions visibilityActions">
            <div onClick={deleteWidget.bind(this, widget)} className="ptr">
              <SVGIcon name="Delete" />
            </div>
          </Actions>
        </div>
      </Header>
      <Content>
        <ContentDeciderContainer
          isLoading={loading}
          hasContent={!loading && mentions?.length > 0}
          mainContent={
            <>{mentions?.map((mention) => getMentionCard(mention))}</>
          }
          newEmptyContainer
          loadingContent={<Loader />}
          notFoundURL={`${envConfig.imageRoot}svg/not-found.svg`}
        />
      </Content>
    </WidgetWrapper>
  );
}

const MentionItemWrapper = styled.div`
  .mention-box {
    border: 1px solid #e4e4e4;
    border-radius: 0 10px 10px;
  }
  .hover-hand {
    cursor: zoom-in;
  }
`;

const MentionItem = (props) => {
  const { time, addedBy, content, title, handleAction } = props;
  const randomId = generateString(5) || 'abcde';

  const handleEscPress = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (e?.keyCode === 27 || e?.which === 27 || e?.key === 'Escape') {
      if (
        window.getComputedStyle(document.querySelector(`.modal-inline-images`))
          .display === 'flex'
      ) {
        document.querySelector(`.modal-inline-images`).style.display = 'none';
      }
      window.removeEventListener('keyup', handleEscPress);
    }
  };

  const imageDownloader = (e) => {
    // window.open(e.target.currentSrc, '_blank').focus();
    const modal = document.querySelector(`.modal-inline-images`);

    const modalImg = document.querySelector(`.modal-content-inline-images`);

    modal.style.display = 'flex';
    modalImg.src = e.target.currentSrc;
    window.addEventListener('keyup', handleEscPress);
  };

  useEffect(() => {
    if (
      document?.querySelector(`.${randomId}`)?.querySelectorAll('img')?.length
    ) {
      document
        ?.querySelector(`.${randomId}`)
        ?.querySelectorAll('img')
        ?.forEach((img) => {
          img.classList.add('hover-hand', 'mw100', 'pointer-events-reset');
          img.addEventListener('click', imageDownloader);
        });
      return () => {
        document
          ?.querySelector(`.${randomId}`)
          ?.querySelectorAll('img')
          ?.forEach((img) => {
            img.classList.remove('hover-hand');
            img.removeEventListener('click', imageDownloader);
          });
      };
    }
  }, []);

  return (
    <MentionItemWrapper className="ml-20 mr-20 mb-20">
      <div className="flex astart">
        <Avatar
          size="medium"
          initials={`${addedBy?.firstName?.[0]}${
            addedBy?.lastName?.[0] ? addedBy?.lastName?.[0] : ''
          }`}
        />
        <div className="ml-10 f11">
          <div className="flex mt-10 mb-10">
            <span className="bold">{addedBy?.name}</span>
            &nbsp;
            <span>
              <FormattedMessage id="admin.home.widgetlibrary.mentions.mention.description" />
            </span>
            &nbsp;
            <Button id={'mentionWidgetHandle'} plain onClick={handleAction}>
              {title}
            </Button>
          </div>
          <div className="mention-box p20">
            <div className="flex fx-sb actr">
              <P2T2 className="flex actr">
                <SVGIcon name="Notes" size="sm" />
                <div className="ml-10 flex">
                  <FormattedMessage id="admin.home.widgetlibrary.mentions.note.description" />
                  &nbsp;
                  <span className="bold">{addedBy?.name}</span>
                </div>
              </P2T2>
              <P2T2>
                <DateTimeFormatter type="dateTime" date={time} />
              </P2T2>
            </div>
            <p
              className={`mt-20 ${randomId}`}
              dangerouslySetInnerHTML={{
                __html: dompurifier(content)
              }}
            />
          </div>
        </div>
      </div>
      <ImagePreviewModal />
    </MentionItemWrapper>
  );
};

export default withErrorBoundary(MentionViewWidget);
